// The player's settings: cookie `opt` (URI-encoded JSON {skin, option}, 30 days, host-only) with a
// localStorage copy `solitaire_storage:options:opt` (raw JSON). Same format as the legacy app, so
// existing visitors keep their settings. See .ai/ui-and-options.md "Storage keys".
// Unlike legacy, bad JSON or unknown keys never stop the app: defaults are used and the extra keys
// are kept for the write-back.
import type { EffectsPref } from '../ui/effects';
import { normalizeGameId } from '../engine/rules';
import { currentLanguage } from '../i18n';
import type { AutoMoveOption, GameId, Scoring } from '../engine/types';

export type SoundBank = 'paper' | 'plastic' | 'off';

export interface GameOptionValues {
  game: GameId;
  scoring: Scoring;
  autoMove: AutoMoveOption;
  autoFlip: boolean;
  sound: SoundBank;
  showTime: boolean;
  showScore: boolean;
  showMoves: boolean;
  /** No-distraction mode (owner 2026-10-18, default off): the top bar keeps only Menu and Undo, the dock only
   *  the Privacy entry (consent must stay reachable). Stored only when on (`focusMode: 1`), so the stored
   *  options of everyone else keep the legacy shape. */
  focusMode: boolean;
  /** The dock folded away (owner 2026-10-23): only its fold button and the Privacy entry stay. Set by the
   *  Options checkbox and by the dock's own fold button; stored only when on (`dockHidden: 1`). */
  dockHidden: boolean;
  /** The turn-three waste in a tilted fan (owner 2026-10-24, default on). Off: the three always lie straight,
   *  each far enough from the next to read its rank (board/geometry.ts). Stored only when off (`wasteFan: 0`). */
  wasteFan: boolean;
  /** The "No more moves" notice (owner 2026-10-26): a window opens when the game in play can no longer be won
   *  (ui/lostGame.ts). On by default from 2026-10-30 to 2026-10-31, then off again (owner, after a player: "to pasjans,
   *  czasem się układa karty dla samej zabawy"); a multiplayer race always has it. Stored only when on
   *  (`noMovesNotice: 1`); a `0` stored on 2026-10-30/31 reads as off, which it is. */
  noMovesNotice: boolean;
  /** The column spacing of every game (owner 2026-10-24): 'narrow' stands the columns side by side, 'wide' spreads
   *  them; 'auto' (not chosen) = narrow in the Classic mode, wide with the 3D effects. Stored only when chosen
   *  (`columns: 'wide' | 'narrow'`). */
  columns: ColumnsPref;
  /** 3D effects (ui/effects.ts): 'auto' = on unless the device looks slow. Stored only when chosen (`effects:
   *  'on' | 'off'`), so the stored options of everyone else keep the legacy shape. */
  effects: EffectsPref;
  /** Language code (35 codes, e.g. `pl-pl`); empty = detect from the browser. */
  lang: string;
  showOnStartup: boolean;
}

export type ColumnsPref = 'auto' | 'wide' | 'narrow';

export interface SkinValues {
  deckType: string;
  cardBackType: string;
  backgroundType: string;
  themeType: string;
  themeAuthor?: string;
  /** Mahjong's tile set picked on the Themes window's Advanced tab (owner 2026-10-31), a name of assets.ts TILE_SETS;
   *  unset = the set of the deck (a theme pick unsets it). */
  tileType?: string | undefined;
  /** Kulki's ball set picked on the same tab (owner 2026-11-03), a name of the same list; unset = the set of the deck. */
  ballType?: string | undefined;
}

export interface StoredOptions {
  option: GameOptionValues;
  skin: SkinValues;
  /** Unknown top-level keys, kept as they were (written back unchanged). */
  extra: Record<string, unknown>;
}

export const COOKIE_NAME = 'opt';
export const STORAGE_KEY = 'solitaire_storage:options:opt';
const COOKIE_DAYS = 30;

export function defaultOptions(env: { mobile: boolean; narrow: boolean; target: string }): StoredOptions {
  return {
    // Key order = the legacy init.js order: the cookie JSON is compared verbatim by the tests.
    option: {
      game: 'klondike:turn-one',
      sound: env.mobile ? 'off' : 'paper',
      scoring: 'timed',
      autoMove: 'when_won',
      autoFlip: true,
      showOnStartup: true,
      showTime: true,
      showScore: true,
      showMoves: true,
      focusMode: false,
      dockHidden: false,
      wasteFan: true,
      noMovesNotice: false,
      columns: 'auto',
      effects: 'auto',
      lang: '',
    },
    skin: {
      deckType: env.mobile && env.narrow ? 'classic_old_v2' : 'classic_old',
      cardBackType: 'classic_old',
      backgroundType: env.target === 'zboze' ? 'classic_old_zboze' : 'classic_old',
      themeType: 'classic_old',
    },
    extra: {},
  };
}

const asBool = (v: unknown, d: boolean): boolean => (v === true || v === 1 || v === '1' || v === 'true' ? true : v === false || v === 0 || v === '0' || v === 'false' ? false : d);
const asString = (v: unknown, d: string): string => (typeof v === 'string' ? v : d);

/** Merge a parsed `{option, skin}` object over the defaults, value by value, tolerating junk. */
export function normalizeOptions(raw: unknown, defaults: StoredOptions): StoredOptions {
  const out: StoredOptions = { option: { ...defaults.option }, skin: { ...defaults.skin }, extra: {} };
  if (!raw || typeof raw !== 'object') return out;
  const r = raw as Record<string, unknown>;
  const o = (r.option && typeof r.option === 'object' ? r.option : {}) as Record<string, unknown>;
  const s = (r.skin && typeof r.skin === 'object' ? r.skin : {}) as Record<string, unknown>;
  out.option.game = normalizeGameId(o.game ?? defaults.option.game);
  out.option.scoring = o.scoring === 'standard' ? 'standard' : o.scoring === 'timed' ? 'timed' : defaults.option.scoring;
  // Legacy semantics: anything that is not 'off' means on (KL-M01).
  out.option.autoMove = o.autoMove === undefined ? defaults.option.autoMove : o.autoMove === 'off' ? 'off' : 'when_won';
  out.option.autoFlip = asBool(o.autoFlip, defaults.option.autoFlip);
  // the legacy `wood` bank is `paper` now (owner 2026-10-17: the sounds are made in code, sound/synth.ts)
  out.option.sound = o.sound === 'paper' || o.sound === 'wood' ? 'paper' : o.sound === 'plastic' || o.sound === 'off' ? o.sound : o.sound === undefined ? defaults.option.sound : 'off';
  out.option.showTime = asBool(o.showTime, defaults.option.showTime);
  out.option.showScore = asBool(o.showScore, defaults.option.showScore);
  out.option.showMoves = asBool(o.showMoves, defaults.option.showMoves);
  out.option.focusMode = asBool(o.focusMode, defaults.option.focusMode);
  out.option.dockHidden = asBool(o.dockHidden, defaults.option.dockHidden);
  out.option.wasteFan = asBool(o.wasteFan, defaults.option.wasteFan);
  out.option.noMovesNotice = asBool(o.noMovesNotice, defaults.option.noMovesNotice);
  out.option.columns = o.columns === 'wide' || o.columns === 'narrow' ? o.columns : 'auto';
  out.option.effects = o.effects === 'on' || o.effects === 'off' ? o.effects : 'auto';
  out.option.lang = asString(o.lang, defaults.option.lang).toLowerCase();
  out.option.showOnStartup = asBool(o.showOnStartup, defaults.option.showOnStartup);
  out.skin.deckType = asString(s.deckType, defaults.skin.deckType);
  out.skin.cardBackType = asString(s.cardBackType, defaults.skin.cardBackType);
  out.skin.backgroundType = asString(s.backgroundType, defaults.skin.backgroundType);
  out.skin.themeType = asString(s.themeType, defaults.skin.themeType);
  if (typeof s.themeAuthor === 'string') out.skin.themeAuthor = s.themeAuthor;
  if (typeof s.tileType === 'string' && s.tileType) out.skin.tileType = s.tileType;
  if (typeof s.ballType === 'string' && s.ballType) out.skin.ballType = s.ballType;
  for (const [k, v] of Object.entries(r)) if (k !== 'option' && k !== 'skin') out.extra[k] = v;
  return out;
}

function readCookie(name: string): string | null {
  const m = document.cookie.split('; ').find((c) => c.startsWith(name + '='));
  return m ? m.slice(name.length + 1) : null;
}

function parseJson(text: string | null): unknown {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function readLocal(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Cookie first; if it does not decode to an object, the localStorage copy. */
export function readStoredOptions(defaults: StoredOptions): { options: StoredOptions; found: boolean } {
  const cookie = readCookie(COOKIE_NAME);
  let raw: unknown = null;
  if (cookie !== null) {
    try {
      raw = parseJson(decodeURIComponent(cookie));
    } catch {
      raw = null;
    }
  }
  if (!raw || typeof raw !== 'object') raw = parseJson(readLocal(STORAGE_KEY));
  const found = !!raw && typeof raw === 'object';
  return { options: normalizeOptions(raw, defaults), found };
}

/** Write both copies in the legacy format (the cookie expiry is renewed on every save). */
export function writeStoredOptions(o: StoredOptions): void {
  // Legacy wrote the checkbox options as 1/0 (options.js); `showOnStartup` stayed a boolean.
  const n = (b: boolean) => (b ? 1 : 0);
  // An unset language is stored as the one in use (legacy froze the browser language on the first save).
  const { focusMode, dockHidden, wasteFan, noMovesNotice, columns, effects, ...rest } = o.option;
  const option = { ...rest, lang: o.option.lang || currentLanguage(), autoFlip: n(o.option.autoFlip), showTime: n(o.option.showTime), showScore: n(o.option.showScore), showMoves: n(o.option.showMoves), ...(focusMode ? { focusMode: 1 } : {}), ...(dockHidden ? { dockHidden: 1 } : {}), ...(wasteFan ? {} : { wasteFan: 0 }), ...(noMovesNotice ? { noMovesNotice: 1 } : {}), ...(columns !== 'auto' ? { columns } : {}), ...(effects !== 'auto' ? { effects } : {}) };
  const json = JSON.stringify({ ...o.extra, skin: o.skin, option });
  const expires = new Date(Date.now() + COOKIE_DAYS * 86400 * 1000).toUTCString();
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(json)}; expires=${expires}; path=/`;
  try {
    localStorage.setItem(STORAGE_KEY, json);
  } catch {
    /* no storage: the cookie is enough */
  }
}
